import {useTranslation} from 'react-i18next'
import {Tag} from '@/components/ui/tag'
import {HelpIcon} from '@/components/help-icon'
import {RejillaMensual} from '@/components/rejilla-mensual'
import type {Evidence} from '@/hooks/sgsst/evidencias/use-evidence'
import type {SoporteCasilla} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {formatDateOnly} from '@/lib/risk'

const ESTADO_TONE: Record<SoporteCasilla['estado'], 'neutral' | 'green' | 'amber' | 'red'> = {
  falta: 'neutral',
  cargado: 'green',
  vence_pronto: 'amber',
  vencido: 'red',
  no_aplica: 'neutral',
}

/** `como_se_ve` es el dato del bloque, no una explicación (T15), así que no se pliega. El criterio
 * person_scoped va una vez en la cabecera, no por fila. */
export function SoportesEsperadosBlock({
  businessId,
  standardCode,
  soportes,
  items,
  editable,
  onUploadMes,
}: {
  businessId: string
  standardCode: string
  soportes: SoporteCasilla[]
  items: Evidence[]
  editable: boolean
  onUploadMes: (soporteKey: string, mes: number) => void
}) {
  const {t} = useTranslation()
  if (soportes.length === 0) return null
  return (
    <div className="rounded-lg bg-secondary/50 px-3 py-2.5" data-testid={`soportes-block-${standardCode}`}>
      <p className="flex items-center gap-1 font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
        {t('evidence.soportes.blockTitle')}
        {soportes.some((casilla) => casilla.person_scoped) && (
          <HelpIcon label={t('evidence.soportes.personScopedTipLabel')} testId="soporte-person-scoped-hint">
            {t('evidence.soportes.personScopedHint')}
          </HelpIcon>
        )}
      </p>
      <ul className="mt-1.5 space-y-2">
        {soportes.map((casilla) => (
          <li key={casilla.key} data-testid={`soporte-casilla-${casilla.key}`} className="text-xs">
            <div className="flex flex-wrap items-center gap-2">
              {/* For cadencia_mensual the rejilla replaces this Tag: both would conflict. */}
              {!casilla.cadencia_mensual && (
                <Tag tone={ESTADO_TONE[casilla.estado]} data-testid="soporte-estado">
                  {t(`evidence.soportes.estado.${casilla.estado}`)}
                </Tag>
              )}
              <span className="font-medium text-foreground">{casilla.titulo}</span>
              {casilla.person_scoped && (
                <Tag tone="neutral" data-testid="soporte-person-scoped-tag">
                  {t('evidence.soportes.personScopedTag')}
                </Tag>
              )}
              {casilla.vence_el && (
                <span className="font-mono text-[10px] text-muted-foreground">
                  {t('evidence.soportes.venceEl', {date: formatDateOnly(casilla.vence_el)})}
                </span>
              )}
              {casilla.person_scoped && casilla.sin_cubrir != null && casilla.sin_cubrir > 0 && (
                <span className="font-mono text-[10px] text-muted-foreground">
                  {t('evidence.soportes.sinCubrir', {count: casilla.sin_cubrir})}
                </span>
              )}
            </div>
            {casilla.cadencia_mensual && casilla.rejilla && (
              <RejillaMensual
                businessId={businessId}
                celdas={casilla.rejilla}
                items={items.filter((item) => item.soporte_key === casilla.key)}
                editable={editable}
                onUpload={(month) => onUploadMes(casilla.key, month)}
              />
            )}
            <p className="mt-0.5 text-muted-foreground" data-testid={`soporte-como-se-ve-${casilla.key}`}>
              {casilla.como_se_ve}
            </p>
            <details className="mt-0.5">
              <summary className="w-fit cursor-pointer text-[11px] text-muted-foreground hover:text-foreground">
                {t('evidence.soportes.verNorma')}
              </summary>
              <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">{casilla.cita}</p>
            </details>
          </li>
        ))}
      </ul>
    </div>
  )
}
